import { useCallback, useEffect, useRef, useState } from 'react';

import { callOrgAvailability } from './orgSetupUtils';

export const ORG_NAME_TAKEN_MESSAGE =
  'An organization with this name already has a CharityStack account';
export const ORG_SLUG_TAKEN_MESSAGE = 'This slug is taken';
export const ORG_AVAILABILITY_ERROR_MESSAGE =
  'Unable to check availability. Please try again.';

const INITIAL_STATE = {
  checking: false,
  checked: false,
  nameError: '',
  slugError: '',
  requestError: '',
  checkedName: '',
  checkedSlug: '',
};

export function useOrgAvailability({
  orgName,
  organizationSlug,
  checkAvailability = callOrgAvailability,
  debounceMs = 350,
}) {
  const requestIdRef = useRef(0);
  const [availability, setAvailability] = useState(INITIAL_STATE);
  const [retryVersion, setRetryVersion] = useState(0);
  const normalizedName = (orgName || '').trim();
  const normalizedSlug = (organizationSlug || '').trim().toLowerCase();
  const retry = useCallback(() => setRetryVersion((version) => version + 1), []);

  useEffect(() => {
    const name = normalizedName;
    const slug = normalizedSlug;
    const requestId = ++requestIdRef.current;

    if (!name) {
      setAvailability(INITIAL_STATE);
      return undefined;
    }

    if (!slug) {
      setAvailability({
        ...INITIAL_STATE,
        checked: true,
        slugError: 'Organization Slug is required',
      });
      return undefined;
    }

    setAvailability({
      ...INITIAL_STATE,
      checking: true,
    });

    const timeoutId = setTimeout(async () => {
      try {
        const result = await checkAvailability({
          orgName: name,
          organizationSlug: slug,
        });
        if (requestId !== requestIdRef.current) return;
        if (
          typeof result?.nameTaken !== 'boolean' ||
          typeof result?.slugTaken !== 'boolean' ||
          (result?.slugError !== null && typeof result?.slugError !== 'string')
        ) {
          throw new Error('Invalid organization availability response');
        }

        setAvailability({
          checking: false,
          checked: true,
          nameError: result.nameTaken ? ORG_NAME_TAKEN_MESSAGE : '',
          slugError: result.slugError || (result.slugTaken ? ORG_SLUG_TAKEN_MESSAGE : ''),
          requestError: '',
          checkedName: name,
          checkedSlug: slug,
        });
      } catch {
        if (requestId !== requestIdRef.current) return;
        setAvailability({
          ...INITIAL_STATE,
          checked: true,
          requestError: ORG_AVAILABILITY_ERROR_MESSAGE,
        });
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [checkAvailability, debounceMs, normalizedName, normalizedSlug, retryVersion]);

  return {
    ...availability,
    retry,
    canContinue:
      availability.checked &&
      !availability.checking &&
      availability.checkedName === normalizedName &&
      availability.checkedSlug === normalizedSlug &&
      !availability.nameError &&
      !availability.slugError &&
      !availability.requestError,
  };
}
